/**
 * Polish content for the six guided arcs.
 *
 * Source: Marta Stankiewicz, Drive doc "03 — Arc Session Content", returned
 * 2026-07-26. All 34 sessions across the six arcs, translated from scratch
 * rather than reviewed — this is her writing, not a correction pass.
 *
 * WHAT IS AND IS NOT TRANSLATED
 * `context` is deliberately absent from this file. It is an instruction to the
 * facilitation model, not user-facing copy, and Marta's brief was explicit that
 * it stays in English. localizeSeries() therefore only ever swaps the three
 * user-visible fields and leaves `context` untouched.
 *
 * GENDERED FORMS
 * Marta asked for a singular neutral form over dual "gotowy/gotowa"
 * constructions (Drive doc 04). She applied it in most places; the handful that
 * still carried dual forms are neutralised here following her own patterns
 * ("Masz za sobą…", present tense, recasting to the thing rather than the
 * person). Each one is marked NEUTRALISED below. Those specific lines are not
 * Marta's words and need her sign-off before launch.
 *
 * ARC 5 NAME
 * Her document gave two different Polish names for "Old Enough to Know Better":
 * "WTEDY I DZIŚ" in the section heading and "Praca z poczuciem winy" in the
 * arc-name list. Andy chose "Wtedy i dziś" (2026-08-28).
 *
 * NOT YET TRANSLATED
 * The long-form `description` for each arc was not in Marta's packet. Rather
 * than machine-translate marketing copy or leave English text sitting inside a
 * Polish UI, each arc falls back to her own theme line, which reads correctly
 * as a short description. Flagged for her to expand.
 */
import type { Series } from './series'

/** The user-visible fields of a single arc session. `context` is never localized. */
export interface SeriesSessionPl {
  focus_title: string
  intention: string
}

/** Polish overrides for one arc, keyed by session position (1-based). */
export interface SeriesPl {
  title: string
  description: string
  tag: string
  theme: string
  sessions: Record<number, SeriesSessionPl>
}

export const SERIES_PL: Record<string, SeriesPl> = {
  // ── Arc 1 · The Forgiveness Work (7 sessions) ────────────────────────────
  forgiveness: {
    title: 'Praca nad wybaczeniem',
    tag: 'Relacje',
    theme: 'Od obwiniania i zranienia do uwolnienia',
    description: 'Od obwiniania i zranienia do uwolnienia — siedem sesji.',
    sessions: {
      1: {
        focus_title: 'Złość ukryta pod historią',
        intention: 'Wydarzyło się coś, co nadal ma nad Tobą władzę. Dziś zaczynamy od prostego nazwania tego — bez szukania jeszcze rozwiązania, po prostu ze szczerością wobec tego, co w sobie nosisz.',
      },
      2: {
        focus_title: 'Opowieść, którą sobie powtarzasz',
        intention: 'Nosisz w sobie pewną wersję tego, co się stało. Dziś przyjrzymy się samej tej historii — nie po to, by ją oceniać, ale by zobaczyć ją wyraźnie.',
      },
      3: {
        focus_title: 'Twój udział w tej historii',
        intention: 'Nie chodzi tu o szukanie winnych. Chodzi o szczere spojrzenie na własną rolę — to, co wnosisz, czego pragniesz i czego próbujesz chronić.',
      },
      4: {
        focus_title: 'Czego chroni Twój żal',
        intention: 'Możliwe, że trzymanie się tego w jakiś sposób czemuś służyło. Dziś z ciekawością przyjrzymy się temu, czym jest ta ochrona.',
      },
      5: {
        focus_title: 'Cena trzymania się przeszłości',
        intention: 'Nierozwiązany żal może wiele kosztować. Dziś przyjrzymy się szczerze temu, jaka cena może się z tym wiązać.',
      },
      6: {
        focus_title: 'Czym wybaczenie NIE jest',
        intention: 'Wybaczenie nie oznacza uznania, że to, co się stało, było w porządku. Dziś oddzielimy ból krzywdy od tego, co oznacza jej uwolnienie.',
      },
      7: {
        focus_title: 'Uwolnienie',
        // NEUTRALISED — Marta: "Wykonałeś/aś ważną pracę." Recast with her own
        // "Masz za sobą…" construction, used elsewhere in her copy.
        intention: 'Masz za sobą ważną pracę. To jest sesja, w której coś naprawdę może się zmienić — nie dlatego, że musisz, ale dlatego, że może pojawić się na to gotowość.',
      },
    },
  },

  // ── Arc 2 · Meeting the Inner Critic (5 sessions) ────────────────────────
  'inner-critic': {
    title: 'Spotkanie z Wewnętrznym Krytykiem',
    tag: 'Relacja ze sobą',
    // NEUTRALISED — Marta: "…że jesteś niewystarczający/niewystarczająca".
    // Recast to the judgement rather than the person, which removes the gender.
    theme: 'Zrozumienie głosu, który mówi, że to wciąż za mało',
    description: 'Zrozumienie głosu, który mówi, że to wciąż za mało — pięć sesji.',
    sessions: {
      1: {
        focus_title: 'Głos, który słyszysz',
        intention: 'Jest w Tobie głos, który przemawia w bardzo konkretny sposób — o tym, co trzeba było zrobić inaczej, kim powinno się być i jak bardzo się nie udaje. Dziś po prostu posłuchamy, co on naprawdę mówi. Nie po to, by się z nim zgodzić — po prostu po to, by go wreszcie wyraźnie usłyszeć.',
      },
      2: {
        focus_title: 'Do kogo należy ten głos?',
        intention: 'Ten głos nie pojawił się znikąd. Został zapamiętany w konkretnym miejscu, w konkretnym czasie i od kogoś konkretnego. Dziś poszukamy jego źródeł — nie po to, by obwiniać, ale by zobaczyć, że ten krytyczny głos być może nigdy tak naprawdę nie był Twój.',
      },
      3: {
        focus_title: 'Jaką rolę chce odgrywać?',
        intention: 'Możliwe, że ta krytykująca część głęboko wierzy, że pomaga. Może uważać, że dba o Twoje bezpieczeństwo, zatrzymuje Cię w miejscu, by chronić przed porażką, lub dba o to, by zasłużyć na miłość. Dziś przyjrzymy się temu z ciekawością — czego tak naprawdę ten głos próbuje chronić?',
      },
      4: {
        focus_title: 'Koszty krytyki',
        intention: 'Słuchanie tego głosu ma swoje konsekwencje — w ryzyku, którego nie udało się podjąć, w częściach Ciebie, które nadal pozostają ukryte, oraz w ciągłym wycofywaniu się. Dziś spójrzmy szczerze na te koszty.',
      },
      5: {
        focus_title: 'Nowa relacja',
        intention: 'Celem nie jest uciszenie tego głosu na siłę — on tak po prostu nie zniknie. Celem jest sprawienie, by przestał mieć ostatnie słowo. Dziś pracujemy nad czymś nowym: ani nad uległością, ani nad walką, lecz nad relacją, w której ten głos nie ma już nad Tobą władzy.',
      },
    },
  },

  // ── Arc 3 · Through Loss (6 sessions) ────────────────────────────────────
  grief: {
    title: 'Przez stratę',
    tag: 'Żałoba',
    theme: 'Przechodzenie przez stratę i żal w ich różnych formach',
    description: 'Przechodzenie przez stratę i żal w ich różnych formach — sześć sesji.',
    sessions: {
      1: {
        focus_title: 'To, co zostało utracone',
        intention: 'Strata jest często czymś znacznie szerszym, niż chcemy przyznać. Jest ta oczywista strata — i są wszystkie mniejsze, które przyszły wraz z nią i których rzadko dotykamy. Dziś damy sobie czas, aby nazwać je wszystkie.',
      },
      2: {
        focus_title: 'To, za czym tęsknisz najbardziej',
        intention: 'Czym innym jest wiedzieć, co się straciło, a czym innym wypowiedzieć na głos, za czym dokładnie się tęskni — za drobnymi, unikalnymi rzeczami, o których wiesz tylko Ty. Dziś dotkniemy tych konkretnych szczegółów, bo to w nich żyje prawdziwy żal.',
      },
      3: {
        focus_title: 'Złość w żałobie',
        intention: 'W żałobie bardzo często pojawia się złość — na samą stratę, na to, co zostało odebrane, na to, co powinno wyglądać inaczej. Nie zawsze łatwo dać sobie na nią prawo. Dziś jest na to bezpieczna przestrzeń.',
      },
      4: {
        focus_title: 'Co ta strata ze sobą przyniosła',
        intention: 'Nie chodzi tu o szukanie dobrych stron na siłę. Chodzi o szczere — i czasem bolesne — pytanie o to, co ta strata umożliwiła, co wyostrzyła lub co otworzyła w Tobie. Nie dlatego, że była warta tej ceny. Po prostu dlatego, że obie te prawdy mogą istnieć obok siebie.',
      },
      5: {
        focus_title: 'Niosąc to dalej',
        // Typo fix only: Marta wrote "ze ciężaru" → "z ciężaru".
        intention: 'Ból po stracie nie znika całkowicie. Zmienia jednak swój kształt — z ciężaru, który przygniata, w coś, co niesie się inaczej. Dziś przyjrzymy się temu, jak to niesiesz, co chcesz zachować, a co być może można już wreszcie odłożyć.',
      },
      6: {
        focus_title: 'Nadal tu jesteś',
        intention: 'Masz za sobą trudne doświadczenie. A jednak wciąż tu jesteś. Dziś nie kończymy sesji idealną kropką — żałoba tak nie działa. Kończymy ją momentem uznania: tego, co Cię utrzymało, tego, co się o sobie dowiadujesz i tego, co czeka na Ciebie dalej.',
      },
    },
  },

  // ── Arc 4 · The Boundary (5 sessions) ────────────────────────────────────
  boundary: {
    title: 'Granica',
    tag: 'Relacje',
    theme: 'Od zadowalania innych i zatracania siebie — z powrotem do siebie',
    description: 'Od zadowalania innych i zatracania siebie, z powrotem do siebie — pięć sesji.',
    sessions: {
      1: {
        focus_title: 'Gdzie tracisz siebie',
        intention: 'W relacjach mogą pojawiać się chwile, kiedy własne potrzeby, granice lub głos schodzą na dalszy plan. Dziś stworzymy przestrzeń, by przyjrzeć się takim doświadczeniom i temu, jak są odczuwane.',
      },
      2: {
        focus_title: 'Czego tak naprawdę się boisz?',
        intention: 'Pod zaspokajaniem potrzeb innych często kryje się lęk — że jeśli przestaniesz to robić, coś ulegnie zniszczeniu. Relacja. Czyjaś opinia o Tobie. Twoje poczucie, że jesteś wartościową osobą. Dziś nazwiemy ten lęk po imieniu.',
      },
      3: {
        // NEUTRALISED — Marta: "Kiedy się tego nauczyłeś/nauczyłaś?". Recast to
        // the lesson, echoing her own "Gdzieś pojawiła się lekcja" below.
        focus_title: 'Kiedy pojawiła się ta lekcja',
        intention: 'Nikt nie rodzi się z nawykiem oddawania siebie innym. Gdzieś pojawiła się lekcja, że to konieczne — by zachować spokój, by zasłużyć na miłość, by czuć się bezpiecznie. Dziś powrócimy do tamtego momentu, by zrozumieć jego dawny sens.',
      },
      4: {
        focus_title: 'Cena przypodobania się',
        intention: 'Rezygnowanie z siebie kosztuje — objawia się w wyczerpaniu, w tłumionym żalu, w tej wersji Ciebie, która ciągle musi pozostawać ukryta. Dziś spójrzmy szczerze na te koszty.',
      },
      5: {
        focus_title: 'Powrót do siebie',
        intention: 'Granica nie jest murem. Jest powrotem — do Twojej własnej prawdy, do tego, czego naprawdę potrzebujesz i kim naprawdę jesteś. Dziś zaczynamy praktykować to odczucie, chociażby w najmniejszy sposób.',
      },
    },
  },

  // ── Arc 5 · Old Enough to Know Better (6 sessions) ───────────────────────
  shame: {
    // Andy chose the section-heading name over the arc-list alternative
    // ("Praca z poczuciem winy") — see file header.
    title: 'Wtedy i dziś',
    tag: 'Wstyd',
    theme: 'Uwalnianie wstydu za przeszłe decyzje i dawną wersję siebie',
    description: 'Uwalnianie wstydu za przeszłe decyzje i dawną wersję siebie — sześć sesji.',
    sessions: {
      1: {
        // NEUTRALISED — Marta: "To, czego sobie nie wybaczyłeś/aś".
        focus_title: 'To, czego sobie nie wybaczasz',
        intention: 'Jest coś, co zostało zrobione — albo zaniechane — co nadal w sobie nosisz. Wybór, chwila, cały okres w życiu. Może padły przeprosiny, zadośćuczynienie, a na zewnątrz wszystko poszło dalej. Ale w środku ta wina wciąż trwa. Dziś to nazwiemy.',
      },
      2: {
        // NEUTRALISED — Marta: "Kim wtedy byłeś/byłaś". Recast to the person
        // being looked at, echoing her own "dawną wersją Ciebie" below.
        focus_title: 'Ta dawna wersja Ciebie',
        intention: 'Osoba, która dokonała tamtego wyboru, była dawną wersją Ciebie — ale niekoniecznie tą samą, która siedzi tu dzisiaj. Miała inną wiedzę, inny ból, inne zasoby. Dziś spójrzmy szczerze na to, kim była tamta osoba i z czym się zmagała.',
      },
      3: {
        // Casing fix: Marta wrote "SamoUkaranie".
        focus_title: 'Samoukaranie',
        // NEUTRALISED — Marta: "wymierzasz samemu/samej sobie".
        intention: 'Wstyd może działać jak kara wymierzana sobie — jakby cierpienie miało zadośćuczynić za to, co się stało, albo uchronić przed ponownym błędem. Dziś przyjrzymy się temu, czy i jak pojawia się to karanie siebie i czy faktycznie działa.',
      },
      4: {
        focus_title: 'Żałowanie a wstyd to nie to samo',
        // NEUTRALISED — Marta: "chciał(a)bym, żeby to poszło inaczej".
        intention: 'Żal mówi: szkoda, że to nie poszło inaczej. Wstyd mówi: jestem do niczego przez to, co się stało. Jeden z nich przynosi mądrość. Drugi po prostu przejmuje kontrolę. Dziś rozdzielimy te dwa odczucia.',
      },
      5: {
        focus_title: 'Czego potrzebowała tamta wersja Ciebie?',
        // Typo fix only: Marta wrote "nie była wskazana na potępienie".
        intention: 'Osoba, która podjęła tamte decyzje, nie była skazana na potępienie. Nie była po prostu zła. Potrzebowała czegoś, czego wtedy nie miała lub nie potrafiła poprosić. Dziś zwrócimy się ku niej z ciekawością, a nie z oceną.',
      },
      6: {
        focus_title: 'Życie z tą historią',
        intention: 'Przeszłości nie da się zmienić. To, co może się zmienić, to Twoja relacja z nią — z czegoś, co Cię definiuje, w część Twojej historii, niesioną ze szacunkiem i spokojem. Dziś zrobimy krok w tym kierunku.',
      },
    },
  },

  // ── Arc 6 · The Next Chapter (5 sessions) ────────────────────────────────
  transitions: {
    title: 'Nowy rozdział',
    tag: 'Przejścia',
    theme: 'Życiowe przełomy i transformacja tożsamości',
    description: 'Życiowe przełomy i transformacja tożsamości — pięć sesji.',
    sessions: {
      1: {
        focus_title: 'To, co się kończy',
        intention: 'Coś w Twoim życiu dobiega końca, skończyło się lub wymaga domknięcia. Zanim zrobisz krok ku temu, co nowe, warto się zatrzymać i nazwać to, co się zamyka — wyraźnie, szczerze i bez pośpiechu.',
      },
      2: {
        // NEUTRALISED — Marta: "Kim byłeś/byłaś do tej pory" (title) and
        // "kim byłeś/aś w tym rozdziale" (intention). Both recast to the
        // version of the self being left behind, which carries no gender.
        focus_title: 'Ta wersja Ciebie, którą zostawiasz',
        intention: 'Zmiany wymagają zostawienia za sobą nie tylko okoliczności, ale też dawnych wersji siebie. Dziś przyjrzymy się temu, jaka wersja Ciebie żyła w tym rozdziale — co Cię definiowało i za czym możesz tęsknić, nawet jeśli masz w sobie gotowość na zmianę.',
      },
      3: {
        focus_title: 'Smutek towarzyszący zmianie',
        intention: 'Nawet gdy zmiana jest dobra i wyczekana, odejściu towarzyszy smutek. To nie oznacza błędnego wyboru. Dziś zrobimy miejsce na ten smutek, bo nie da się iść naprzód z czystą kartą, niosąc ze sobą nieuznaną żałobę.',
      },
      4: {
        focus_title: 'Lęk przed niewiadomą',
        intention: 'Po drugiej stronie progu kryje się niepewność co do tego, kim się staniesz. To, ku czemu idziesz, nie jest jeszcze w pełni ukształtowane. To może budzić lęk, do którego trudno się przyznać. Dziś nazwiemy ten lęk wprost.',
      },
      5: {
        focus_title: 'Świadomy wybór',
        intention: 'Zmiana nie musi być tylko czymś, co niesie jak fala. Na pewnym poziomie można świadomie wybierać to, co nastąpi. Dziś nadamy temu wyborowi jasność — co zabierasz ze sobą, co zostawiasz i kim decydujesz się stać.',
      },
    },
  },
}

/**
 * Returns the arc with its user-visible copy swapped for the requested
 * language, leaving every other field — crucially each session's `context` —
 * exactly as authored in English.
 *
 * Falls through to the original object for English, for an unknown language, or
 * for any arc or session position with no Polish entry, so a missing
 * translation degrades to English text rather than to a blank screen.
 */
export function localizeSeries(series: Series, lang?: string): Series {
  if (lang !== 'pl') return series

  const pl = SERIES_PL[series.id]
  if (!pl) return series

  return {
    ...series,
    title: pl.title,
    description: pl.description,
    tag: pl.tag,
    theme: pl.theme,
    sessions: series.sessions.map(session => {
      const s = pl.sessions[session.position]
      return s
        ? { ...session, focus_title: s.focus_title, intention: s.intention }
        : session
    }),
  }
}
